import { faCheck, faSpinner, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { ApprovalGroup } from "../../../core/models/entities/Approval";
import { Role } from "../../../core/models/entities/Role";
import { ConfirmationGuard } from "../ConfirmationGuard";
import { useRejectLogoDialog } from "../Dialogs/reject-logo-dialog";
import IconButton from "../LogoButton/IconButton";
import { ProductPendingLock } from "../Logo/ProductPendingBadge";
import { LogoActionLockVariant } from "../../../utils/functions/productPending";

export function ApprovalVoting({
  message,
  approvalGroup,
  placeVerdict,
  rejectDelete,
  isForOrders,
  isLoading,
  fullWidth,
  productPending,
  lockVariant,
  rejectAndEdit,
}: {
  message?: string;
  approvalGroup: ApprovalGroup;
  placeVerdict: (verdict: Verdict) => void;
  rejectDelete: (reason?: string) => void;
  // "Reject and fix it myself" for a logo (flag logoRejectAndEditEnabled):
  // when given, the reject dialog offers the third option and calls this
  // with the (optional, trimmed) note. Absent = today's dialog.
  rejectAndEdit?: (reason: string) => void;
  isForOrders?: boolean;
  isLoading?: boolean;
  fullWidth?: boolean;
  // "Prices on their way" (flag productPendingEnabled): true locks Approve
  // (disabled + tooltip) while the logo's product/prices are still owed by
  // the API worker - an approval would hit the API's ProductPendingGuard.
  // Reject stays available (no price involved). Absent/false = the buttons
  // exactly as today (no wrapper, no attribute).
  productPending?: boolean;
  // Which lock tooltip: "upload" (flag cloneInstantEnabled) while the clone
  // still awaits its own supplier identity; absent = the prices text.
  lockVariant?: LogoActionLockVariant;
}) {
  const { t } = useTranslation();
  const { role } = useProfileContext();
  const rejectionDialog = useRejectLogoDialog(
    "",
    (reason) => placeVerdict({ approve: false, rejectionNote: reason }),
    (reason) => rejectDelete(reason),
    () => {},
    isForOrders,
    rejectAndEdit
  );

  function showButtons() {
    if (
      role === Role.EndUser &&
      (approvalGroup.customer?.approver || approvalGroup.customer?.status == 0)
    ) {
      return true;
    }
    if (
      role === Role.Vendor &&
      (!approvalGroup.vendor?.approver || approvalGroup.vendor?.status === 0)
    ) {
      return true;
    }

    return false;
  }

  const show = showButtons();
  const pendingLock = productPending === true;

  const approveButton = (
    <ProductPendingLock locked={pendingLock} variant={lockVariant ?? "product"}>
      <ConfirmationGuard
        anchor={
          <IconButton
            disabled={role === Role.Admin || isLoading || pendingLock}
            key={"approve"}
            variant="success"
            width={fullWidth ? "full" : undefined}
            icon={
              <FontAwesomeIcon
                icon={isLoading ? faSpinner : faCheck}
                spin={isLoading}
              />
            }
          >
            {isLoading ? t("processing") : t("compDialogsInput.approve")}
          </IconButton>
        }
        text={message ?? t("compDialogsInput.confirmApproveLogo")}
        onAccept={(e) => {
          if (pendingLock) return;
          placeVerdict({ approve: true });
        }}
      />
    </ProductPendingLock>
  );

  const rejectButton = (
    <ConfirmationGuard
      anchor={
        <IconButton
          disabled={role === Role.Admin || isLoading}
          key={"reject"}
          variant="error"
          width={fullWidth ? "full" : undefined}
          icon={<FontAwesomeIcon icon={faXmark} />}
        >
          {t("compDialogsInput.reject")}
        </IconButton>
      }
      text={t("compDialogsInput.confirmRejectItem")}
      onAccept={rejectionDialog.open}
    />
  );

  return (
    <>
      {rejectionDialog.component}
      {show && approveButton}
      {show && rejectButton}
    </>
  );
}

export interface Verdict {
  approve: boolean;
  rejectionNote?: string;
  // "Reject and fix it myself" (flag rejectAndEditEnabled): asks the API to
  // unlock the rejected design for the editor (API flag
  // DesignRejectAndEditEnabled). Only ever set by that path, so the body of
  // every other verdict is unchanged.
  editAfterReject?: boolean;
}
